import * as React from "react"
import { InjectedIntlProps, injectIntl, FormattedNumber } from "react-intl"
import { bodyW } from "../pptx-configuration/constants"
import * as CommonUtils from "../utils"
import * as Conf from "../../configuration"
import * as LoanDomain from "../../../../domain/sfp/loan-domain"
import * as Model from "../../../../model"

class DebtTable extends React.Component<Props & InjectedIntlProps> {
	private readonly labelCellWidth: number
	private readonly numberCellWidth: number
	private readonly valueCellWidth: number

	constructor(props: Props & InjectedIntlProps) {
		super(props)

		this.labelCellWidth = CommonUtils.toInches(6.1)
		this.numberCellWidth = CommonUtils.toInches(2)
		this.valueCellWidth = (bodyW - this.numberCellWidth - this.labelCellWidth)
		/ props.years.length
	}

	render() {
		const currency = this.props.currency
		const decomposition = this.props.decomposition
		const decomposedLoans = this.props.decomposedLoans
		const id = this.props.id
		const locale = this.props.locale
		const realDecomposedLoans: Model.LoanWithDecomposition[] = []
		const roundness = this.props.roundness
		const simulatedDecomposedLoans: Model.LoanWithDecomposition[] = []
		const tableConf = this.props.tableConfiguration
		const years = this.props.years

		for (const decomposedLoan of decomposedLoans) {
			if (decomposedLoan.loan.simulate)
				simulatedDecomposedLoans.push(decomposedLoan)
			else realDecomposedLoans.push(decomposedLoan)
		}

		return <table id={id}>
			<thead>
				<tr>
					<th data-pptx-width={this.numberCellWidth}
					style={{ ...tableConf.header }}>N°</th>

					<th data-pptx-width={this.labelCellWidth}
					style={{ ...tableConf.header }}>Objet</th>

					{years.map((year, yearIndex) =>
						<th data-pptx-width={this.valueCellWidth}
						key={yearIndex}
						style={{ ...tableConf.header }}>{year}</th>
					)}
				</tr>
			</thead>

			<tbody>
				{this.createDecomposedLoansRows(
					decomposition,
					realDecomposedLoans,
					years,
					tableConf
				)}
				{this.createDecomposedLoansRows(
					decomposition,
					simulatedDecomposedLoans,
					years,
					tableConf
				)}
			</tbody>

			<tfoot>
				<tr>
					<td style={{ ...tableConf.total }}></td>

					<td style={{ ...tableConf.total }}>Total</td>

					{years.map((year, yearIndex) => {
						const decompositions = decomposedLoans.map(d => d.decomposition)
						let value = 0

						if (Decomposition.Annuity === decomposition)
							value = LoanDomain
							.getTotalAnnuity(decompositions, year)

						if (Decomposition.CapitalReimbursement === decomposition)
							value = LoanDomain
							.getTotalYearReimbursedCapital(decompositions, year)

						if (Decomposition.InterestReimbursement === decomposition)
							value = LoanDomain
							.getTotalYearInterest(decompositions, year)

						if (Decomposition.RemainingCapital === decomposition)
							value = LoanDomain
							.getTotalYearRemainingCapital(decompositions, year)

						return <td key={yearIndex}
						style={{ ...tableConf.total, ...tableConf.numeric }}>
							{CommonUtils.formatCurrency(
								locale, currency, value || 0, roundness)}
						</td>
					})}
				</tr>
			</tfoot>
		</table>
	}

	private createDecomposedLoansRows(
		decomposition: Decomposition,
		decomposedLoans: Model.LoanWithDecomposition[],
		years: number[],
		tableConf: Conf.TableConfiguration
	): React.ReactNode {		
		return decomposedLoans.map((decomposedLoan, loanIndex) => {
			const simulatedStyle = decomposedLoan.loan.simulate
				? { ...tableConf.negative }
				: {}

			return <tr key={loanIndex}>
				<td style={{ ...tableConf.common, ...simulatedStyle }}>
					{decomposedLoan.loan.number}
				</td>

				<td style={{ ...tableConf.common, ...simulatedStyle }}>
					{decomposedLoan.loan.subject}
				</td>

				{years.map((year, yearIndex) => {
					let value = 0

					if (Decomposition.Annuity === decomposition)
						value = LoanDomain.getAnnuity(decomposedLoan.decomposition, year)

					if (Decomposition.CapitalReimbursement === decomposition)
						value = LoanDomain
						.getYearReimboursedCapital(decomposedLoan.decomposition, year)

					if (Decomposition.InterestReimbursement === decomposition)
						value = LoanDomain
						.getYearInterest(decomposedLoan.decomposition, year)

					if (Decomposition.RemainingCapital === decomposition)
						value = LoanDomain
						.getYearRemainingCapital(decomposedLoan.decomposition, year)

					return <td key={yearIndex}
					style={{
						...tableConf.common,
						...simulatedStyle,
						...tableConf.numeric
					}}>
						{CommonUtils.formatCurrency(
							this.props.locale,
							this.props.currency,
							value || 0,
							this.props.roundness)}
					</td>
				})}
			</tr>
		})
	}
}

export default injectIntl(DebtTable)

export enum Decomposition {
	Annuity,
	CapitalReimbursement,
	InterestReimbursement,
	RemainingCapital
}

export interface Props {
	currency: string
	decomposedLoans: Model.LoanWithDecomposition[]
	decomposition: Decomposition
	id: string
	locale: string
	roundness: Conf.RoundnessValue
	tableConfiguration: Conf.TableConfiguration
	years: number[]
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/debt-generator/debt-table.tsx